import { Component, Input, Output, OnChanges, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ILabel } from '../ILabel.model';
import { MultiListBoxItem } from './multi-list-box-item.model';
import { PermissionType } from '../../users-module/permission-groups-data-table/permission-group.model';
import { SelectType } from '../cpq-select/cpq-select.component';
import { AddEditUserService } from '../../users-module/add-edit-user/add-edit-user.service';
import { CpqInputComponent } from '../cpq-input/cpq-input.component';
import { TranslationService } from '../common/services/translation.service';
import { BlockedItems, BlockedItemsType } from './blocked-items.model';
import { clone } from '../common/functions/common-helpers';
import { emptyArray, removeAllIfExist } from '../common/functions/js-linq';

// **
// when making a basic list, (at least) one SelectListParent needs to contain all the SelectListItems
// *
@Component({
    selector: 'cpq-multi-list-box-with-selection',
    templateUrl: './cpq-multi-list-box-with-selection.component.html',
    styleUrls: ['./cpq-multi-list-box-with-selection.component.scss'],
    providers: [AddEditUserService],
    standalone: false
})
export class CpqMultiListBoxWithSelectionComponent implements OnChanges {
  @Input('labelAvailable') labelAvailable: ILabel;
  @Input('labelSelected') labelSelected: ILabel;
  @Input('selected') selected: Array<MultiListBoxItem>;
  @Input('available') available: Array<MultiListBoxItem>;
  @Output('onSelect') onSelect = new EventEmitter<MultiListBoxItem>();
  @Output('onRemove') onRemove = new EventEmitter<MultiListBoxItem>();
  @Input('isCpqPermissions') isCpqPermissions?= false;
  @ViewChild('selectedGroupsInput', { static: true }) selectedGroupsInput: CpqInputComponent;
  @ViewChild('availableGroupsInput', { static: true }) availableGroupsInput: CpqInputComponent;
  @Input('blockedItems') blockedItems: BlockedItems;
  @Input('emptySelectedListKey') emptySelectedListKey?: string = 'Admin.SetupSpa.SelectPermissionsGroups';

  public searchKeywordAvailable: string;
  public searchKeywordSelected: string;
  public PermissionType = PermissionType;
  public SelectType = SelectType;
  public userId: number;
  public hasAvailableSearchResults = true;
  public hasSelectedSearchResults = true;
  public showSearchIconAvailable = true;
  public showSearchIconSelected = true;
  public availableItemHeight = 29;
  public selectedItemHeight = 33;
  public availableItemsContainerMaxHeight = 270;
  public selectedItemsContainerMaxHeight = 396;
  public filteredAvailableItems: Array<MultiListBoxItem>;
  public filteredSelectedItems: Array<MultiListBoxItem> = new Array<MultiListBoxItem>();

  constructor(public route: ActivatedRoute,
    public addEditUserService: AddEditUserService,
    private translationService: TranslationService) { }

  ngOnChanges() {
    if (this.available && this.available.length > 0 && this.selected) {
      this.initAvailableItems(true);
      this.setAllSelectedItemsVisible();

      if (this.searchKeywordAvailable) {
        this.searchAvailable(); // this function is called if search bar for available group is not empty to show all filtered items
      }
    }
  }

  /**
    We hide from available items, those items who has been previously selected
    */
  private initAvailableItems(initFilteredItems: boolean) {
    this.available.forEach(availableGroup => {
      availableGroup.isVisible = true;
      if (availableGroup && availableGroup.children && availableGroup.children.length > 0) {
        availableGroup.children.forEach(item => {
          item.parentId = availableGroup.id; // double check, it should always be item.parentId === group.id
          item.isVisible = this.selected.find(x => x.parentId === item.parentId && x.id === item.id) ? false : true;
        });
      }
    });

    if (initFilteredItems) {
      this.filteredAvailableItems = this.getFilteredItems(this.available);
    }
  }

  setAllSelectedItemsVisible() {
    this.selected.forEach(item => item.isVisible = true);
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  searchAvailable() {
    this.initAvailableItems(false);
    this.hasAvailableSearchResults = false;
    if (this.searchKeywordAvailable !== '') {
      this.available.forEach(availableGroup => {
        let isGroupItemFound = false;
        availableGroup.children.filter(x => x.isVisible).forEach(child => {
          if (child.name.toLowerCase().indexOf(this.searchKeywordAvailable.toLowerCase()) > -1) {
            this.hasAvailableSearchResults = true;
            isGroupItemFound = true;
          } else {
            child.isVisible = false;
          }
        });
        this.showSearchIconAvailable = false;
        availableGroup.isVisible = isGroupItemFound;
        availableGroup.isCollapsed = false;
        let filteredItem = this.filteredAvailableItems.find(x => x.id === availableGroup.id);
        if (filteredItem) {
          filteredItem.isCollapsed = false;
        }
      });
    } else {
      this.showSearchIconAvailable = true;
      this.hasAvailableSearchResults = true;
      this.collapseAllGroups();
    }
    this.filteredAvailableItems = this.getFilteredItems(this.available);
  }

  private getFilteredItems(items: Array<MultiListBoxItem>): Array<MultiListBoxItem> {
    const filteredItems = new Array<MultiListBoxItem>();
    items.filter(x => x.isVisible).forEach(item => {
      const filteredGroup = clone(item, true);
      
      if (item.children && item.children.length) {
        filteredGroup.children = item.children.filter(child => child.isVisible);
      }

      if (this.filteredAvailableItems) {
        let filteredAvailableItem = this.filteredAvailableItems.find(x => x.id === filteredGroup.id);
        if (filteredAvailableItem) {
          filteredGroup.isCollapsed = filteredAvailableItem.isCollapsed;
        }
      }

      filteredItems.push(filteredGroup);
    });

    return filteredItems;
  }

  private collapseAllGroups() {
    this.available.forEach(availableGroup => {
      availableGroup.isVisible = true;
      availableGroup.isCollapsed = true;
    });
    this.filteredAvailableItems.forEach(availableGroup => {
      availableGroup.isVisible = true;
      availableGroup.isCollapsed = true;
    });
  };

  searchSelected() {
    this.selected.forEach(item => { item.isVisible = true; });
    this.hasSelectedSearchResults = false;
    if (this.searchKeywordSelected !== '') {
      this.showSearchIconSelected = false;
      this.selected.forEach(item => {
        if ((item.name.toLowerCase()).indexOf(this.searchKeywordSelected.toLowerCase()) === -1) {
          item.isVisible = false;
        } else {
          this.hasSelectedSearchResults = true;
        }
      });
    } else {
      this.showSearchIconSelected = true;
      this.hasSelectedSearchResults = true;
    }
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  addSelectedInteractive(item: MultiListBoxItem, liref: HTMLLIElement) {
    liref.style.paddingLeft = '100%';
    setTimeout(() => {
      this.addSelected(item);
    }, 300);
  }

  addSelected(item: MultiListBoxItem, shouldUpdateFilteredItems: boolean = true) {
    if (!this.selected.find(x => x.id === item.id && x.parentId === item.parentId)) {
      const foundItem = this.getItemFromAvailableItems(item);
      if (foundItem) {
        foundItem.isVisible = false;
      }
      const selectedItem = clone(item);
      selectedItem.isVisible = true;
      this.selected.push(selectedItem);
      this.onSelect.next(item);

      if (shouldUpdateFilteredItems) {
        this.filteredAvailableItems = this.getFilteredItems(this.available);
        this.filteredSelectedItems = this.getFilteredItems(this.selected);
        this.filteredAvailableItems.find(x => x.id === item.parentId).isCollapsed = false;
      }
    }
  }

  removeSelectedInteractive(item: MultiListBoxItem, selectedItem: HTMLLIElement) {
    selectedItem.style.opacity = '0';
    setTimeout(() => {
      this.removeSelected(item);
      selectedItem.style.opacity = '1';
    }, 200);
  }

  removeSelected(item: MultiListBoxItem) {
    const index: number = this.selected.findIndex(x => x.parentId === item.parentId && x.id === item.id);
    if (index > -1) {
      this.selected.splice(index, 1);
      this.pushToAvailable(item);
      this.filteredAvailableItems = this.getFilteredItems(this.available);
      this.filteredSelectedItems = this.getFilteredItems(this.selected);
      this.onRemove.next(item);
    }
  }

  removeAllSelected() {
    this.selected.forEach(item => {
      if (!item.isDisabled) {
        this.pushToAvailable(item);
        this.onRemove.next(item);
      }
    });
    removeAllIfExist(this.selected, item => !item.isDisabled);
    if (this.searchKeywordAvailable) {
      this.searchAvailable();
    } else {
      this.initAvailableItems(true);
    }

    if (this.searchKeywordSelected) {
      this.searchSelected();
    } else {
      this.filteredAvailableItems = this.getFilteredItems(this.available);
    }

    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  public expandCollapseAllAvailableGroups(isCollapsed: boolean) {
    this.available.forEach(availableGroup => availableGroup.isCollapsed = isCollapsed);
    this.filteredAvailableItems.forEach(availableGroup => availableGroup.isCollapsed = isCollapsed);
  }

  pushToAvailable(item: MultiListBoxItem) {
    const availableItem = this.getItemFromAvailableItems(item);
    if (availableItem) {
      availableItem.isVisible = true;
    } else {
      // in case if it is user, than we ignore this item
    }
  }

  private getItemFromAvailableItems(item: MultiListBoxItem): MultiListBoxItem {
    let foundItem = null;
    this.available.forEach(x => {
      if (!foundItem) {
        x.children.forEach(child => {
          if (child.id === item.id && child.parentId === item.parentId && !foundItem) {
            foundItem = child;
          }
        });
      }
    });
    return foundItem;
  }

  addAllAvailableGroups() {
    this.searchKeywordAvailable = '';
    this.available.forEach(availableGroup => {
      this.addAllChildItemsToSelected(availableGroup.id);
    });
    this.initAvailableItems(true);
  }

  addAllChildItemsToSelected(availableGroupId: number) {
    const allChildItems = this.available.find(group => group.id === availableGroupId).children;
    allChildItems.forEach(item => this.addSelected(item, false));
    this.filteredAvailableItems = this.getFilteredItems(this.available);
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  userSelected(userInfo: string) {
    const info = (this.blockedItems && this.blockedItems.type === BlockedItemsType.Users && this.blockedItems.items.findIndex(id => id === this.userId) > -1) ? this.translationService.translate('Msg.Blocked') : '';
    this.addSelected({ id: this.userId, isVisible: true, name: userInfo, additionalInfo: info, parentId: PermissionType.Users, isSelected: true });
    this.userId = 0;
  }

  emptyInputField(isSelectedGroupsInput: boolean) {
    if (isSelectedGroupsInput) {
      this.selectedGroupsInput.value = '';
      this.selectedGroupsInput.inputField.nativeElement.focus();
    } else {
      this.availableGroupsInput.value = '';
      this.availableGroupsInput.inputField.nativeElement.focus();
    }
  }
}
